import React, {useCallback, useMemo, useState} from 'react';
import {PAYMENT_SYSTEM, TRANSACTION_STATUS, TRANSACTION_TYPE} from "../../utils/constants";
import {Button, Flex, message, Select, Space, Table, Tag, Tooltip, Typography} from "antd";
import classes from "./TransactionTable.module.scss";
import {CloseCircleOutlined} from "@ant-design/icons";
import moment from "moment/moment";
import {useColumnSearchProps} from "../../hooks/useColumnSearchProps";
import {useNavigate} from "react-router-dom";
import {ROUTES} from "../AppRouter/AppRouter";
import {API} from "../../http";
import {NEW_ENDPOINTS} from "../../http/endpoints";

const getStatusLabel = (status) => {
  const colorMap = {
    [TRANSACTION_STATUS.APPROVED]: '#52c41a',
    [TRANSACTION_STATUS.DONE]: '#3c8d13',
    [TRANSACTION_STATUS.DECLINED]: '#f5222d',
    [TRANSACTION_STATUS.PENDING]: '#009be2',
    [TRANSACTION_STATUS.NEW]: '#8f8f8f',
  };
  return <Tag color={colorMap[status] || 'default'}>{status}</Tag>;
};

const TransactionsTable = ({ transactions, loading, excludeColumns = [], onStatusUpdate, ...props }) => {
  const [editableTransactionId, setEditableTransactionId] = useState();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRowKeysStatus, setSelectedRowKeysStatus] = useState(TRANSACTION_STATUS.NEW);
  const [innerLoading, setInnerLoading] = useState(false);

  const {getColumnSearchProps} = useColumnSearchProps();
  const navigate = useNavigate();

  const hasSelected = useMemo(() => selectedRowKeys.length > 0, [selectedRowKeys]);

  const handleChangeStatus = useCallback((ids, new_status) => {
    setInnerLoading(true);
    const idsArray = Array.isArray(ids) ? ids : [ids];
    const updatePromises = idsArray.map(async id => {
      try {
        const {data: { transaction }} = await API.post(NEW_ENDPOINTS.ADMIN.ORDER.UPDATE, {
          order_id: id,
          status: new_status,
        });
        return transaction;
      } catch (e) {
        message.error(`Failed to update status for id ${id}`);
        throw e;
      }
    });
    Promise.allSettled(updatePromises).then(results => {
      const successfulUpdates = results.filter(result => result.status === 'fulfilled').map(result => result.value);

      const updatedTransactions = transactions.map(item => {
        const update = successfulUpdates.find(update => update.id === item.id);
        return update ? update : item;
      });
      setInnerLoading(false);
      onStatusUpdate?.(updatedTransactions);
      setSelectedRowKeys([]);
      setEditableTransactionId(undefined);
    }).catch(error => {
      message.error("An error occurred while updating statuses");
    });
  }, [onStatusUpdate, transactions]);

  const {columns, scrollX} = useMemo(() => {
    const allColumns = [
      {
        title: 'System',
        dataIndex: 'payment_system',
        key: 'payment_system',
        filters: Object.values(PAYMENT_SYSTEM).map(tType => ({
          text: tType,
          value: tType
        })),
        filterMode: 'menu',
        width: 130,
        align: 'center',
        onFilter: (value, record) => record.payment_system.includes(value),
        render: (value) => {
          switch (value) {
            case PAYMENT_SYSTEM.SEPA:
              return <Tag bordered={false} color="cyan">{PAYMENT_SYSTEM.SEPA}</Tag>
            case PAYMENT_SYSTEM.SWIFT:
              return <Tag bordered={false} color="purple">{PAYMENT_SYSTEM.SWIFT}</Tag>
            default:
              return <Tag bordered={false}>Unknown</Tag>
          }
        }
      },
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 200,
        ...getColumnSearchProps('id', (_, record) => {
          return (
            <Typography.Link onClick={() => navigate(`${ROUTES.TRANSACTION.path}/${record.id}`)} copyable>
              {record.id}
            </Typography.Link>
          )
        }),
      },
      {
        title: 'Client name',
        key: 'client_name',
        width: 150,
        render: (value, record) => {
          const fullName = [record?.first_name, record?.last_name].filter(Boolean).join(' ');
          if (!fullName) return <Typography.Text>Unknown</Typography.Text>;
          return (
            <Typography.Link onClick={() => navigate(`${ROUTES.USER.path}/${record.creator_id}`)} copyable ellipsis>
              {fullName}
            </Typography.Link>
          );
        },
      },
      {
        title: 'From account',
        key: 'from_account',
        dataIndex: 'from_account',
        width: 200,
        render: (value) => <Typography.Text copyable={value}>{value || '-/-'}</Typography.Text>,
      },
      {
        title: 'Type',
        dataIndex: 'type',
        key: 'type',
        filters: Object.values(TRANSACTION_TYPE).map(tType => ({
          text: tType,
          value: tType
        })),
        filterMode: 'menu',
        width: 120,
        align: 'center',
        onFilter: (value, record) => record.type.includes(value),
        render: (value) => <Typography.Text>{value}</Typography.Text>
      },
      {
        title: 'Amount',
        dataIndex: 'amount',
        key: 'amount',
        width: 250,
        align: 'center',
        render: (value, record) => {
          return (
            <Flex justify="center" align="center" vertical>
              <Typography.Text>{Number(value)?.toFixed(2) || '-/-'} {record?.currency ?? '?'}</Typography.Text>
              <Space size={5} align="center">
                <Tooltip title="fee">
                  <Typography.Text className={classes.amountMeta}>{Number(record.fee)?.toFixed(2) ?? "N/A"} EUR</Typography.Text>
                </Tooltip>
                <Typography.Text className={classes.amountMeta}> / </Typography.Text>
                <Tooltip title="fee_tesla">
                  <Typography.Text className={classes.amountMeta}>{Number(record.fee_tesla)?.toFixed(2) ?? "N/A"} EUR</Typography.Text>
                </Tooltip>
              </Space>
            </Flex>
          )
        }
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        filters: Object.values(TRANSACTION_STATUS).map(tType => ({
          text: tType,
          value: tType
        })),
        filterMode: 'menu',
        onFilter: (value, record) => record.status === value,
        width: 170,
        align: 'center',
        render: (value, record) => {
          if (record.id === editableTransactionId) return (
            <Space>
              <Select
                defaultValue={value}
                loading={loading}
                style={{width: 100}}
                onChange={(value) => handleChangeStatus(record.id, value)}
                options={Object.values(TRANSACTION_STATUS).map(i => ({title: i, value: i}))}
              />
              <CloseCircleOutlined onClick={() => setEditableTransactionId(undefined)} style={{cursor: 'pointer'}}/>
            </Space>
          )
          return (
            <div onClick={() => setEditableTransactionId(record.id)} style={{cursor: 'pointer'}}>
              {getStatusLabel(value)}
            </div>
          )
        }
      },
      {
        title: 'Bank Direction',
        key: 'bank_direction',
        render: (value, record) => (
          <Typography.Text>{`${record.from_bank_name ?? '�'} → ${record.to_bank_name ?? '�'}`}</Typography.Text>
        )
      },
      {
        title: 'payment_id',
        dataIndex: 'payment_id',
        key: 'payment_id',
        width: 150,
        render: (value) => <Typography.Text copyable={value}>{value || '-/-'}</Typography.Text>
      },
      {
        title: 'Updated',
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        fixed: 'right',
        width: 180,
        render: (value) => <Typography.Text
          ellipsis>{moment(value).format('DD.MM.YYYY, HH:mm:ss')}</Typography.Text>,
        defaultSortOrder: 'descend',
        sorter: (a, b) => {
          const dateA = moment(a.updatedAt).toDate();
          const dateB = moment(b.updatedAt).toDate();
          if (dateA < dateB) {
            return -1;
          } else if (dateA > dateB) {
            return 1;
          } else {
            return 0;
          }
        },
      },
    ];

    const filteredColumns = allColumns.filter(column => !excludeColumns.includes(column.key));
    const scrollX = filteredColumns.reduce((sum, { width }) => sum + width, 0);
    return {columns: filteredColumns, scrollX: scrollX};
  }, [getColumnSearchProps, navigate, editableTransactionId, loading, handleChangeStatus, excludeColumns]);

  const expandedRowRender = (record) => {
    const columns = [
      {
        title: 'From',
        children: [
          {
            title: 'Name',
            dataIndex: 'from_name',
            key: 'from_name',
            width: 150,
            render: (value) => <Typography.Text ellipsis={{tooltip: value}}
                                                copyable={value}>{value}</Typography.Text>
          },
          {
            title: 'Account',
            dataIndex: 'from_account',
            key: 'from_account',
            width: 150,
            render: (value) => <Typography.Text ellipsis={{tooltip: value}}
                                                copyable={value}>{value}</Typography.Text>
          },
          {
            title: 'BIC',
            dataIndex: 'from_bic',
            key: 'from_bic',
            width: 150,
            render: (value) => <Typography.Text ellipsis={{tooltip: value}}
                                                copyable={value}>{value}</Typography.Text>
          },
          {
            title: 'Address',
            dataIndex: 'from_address',
            key: 'from_address',
            width: 150,
            render: (value) => <Typography.Text ellipsis={{tooltip: value}}
                                                copyable={value}>{value}</Typography.Text>
          },
          {
            title: 'Reference',
            dataIndex: 'from_reference',
            key: 'from_reference',
            width: 150,
            render: (value) => <Typography.Text ellipsis={{tooltip: value}}
                                                copyable={value}>{value}</Typography.Text>
          },
        ],
      },
      {
        title: 'To',
        children: [
          {
            title: 'Name',
            dataIndex: 'to_name',
            key: 'to_name',
            width: 150,
            render: (value) => <Typography.Text ellipsis={{tooltip: value}}
                                                copyable={value}>{value}</Typography.Text>
          },
          {
            title: 'Account',
            dataIndex: 'to_account',
            key: 'to_account',
            width: 150,
            render: (value) => <Typography.Text ellipsis={{tooltip: value}}
                                                copyable={value}>{value}</Typography.Text>
          },
          {
            title: 'BIC',
            dataIndex: 'to_bic',
            key: 'to_bic',
            width: 150,
            render: (value) => <Typography.Text ellipsis={{tooltip: value}}
                                                copyable={value}>{value}</Typography.Text>
          },
          {
            title: 'Address',
            dataIndex: 'to_address',
            key: 'to_address',
            width: 150,
            render: (value) => <Typography.Text ellipsis={{tooltip: value}}
                                                copyable={value}>{value}</Typography.Text>
          },
          {
            title: 'Reference',
            dataIndex: 'to_reference',
            key: 'to_reference',
            width: 150,
            render: (value) => <Typography.Text ellipsis={{tooltip: value}}
                                                copyable={value}>{value}</Typography.Text>
          },
        ],
      },
    ];
    return <Table scroll={{x: 1000}} columns={columns} dataSource={[record]} pagination={false} bordered />;
  };

  return (
    <Space className="w100" direction="vertical">
      <Flex justify="space-between" gap={30} className="w100" align="baseline">
        <Space>
          <Select
            loading={loading}
            style={{width: 110}}
            value={selectedRowKeysStatus}
            onChange={setSelectedRowKeysStatus}
            disabled={!hasSelected || loading || innerLoading}
            options={Object.values(TRANSACTION_STATUS).map(i => ({title: i, value: i}))}
          />
          <Button onClick={() => handleChangeStatus(selectedRowKeys, selectedRowKeysStatus)}
                  disabled={!hasSelected || loading || innerLoading}>Update status</Button>
          {hasSelected &&
            <Typography.Text type="secondary">Selected: {selectedRowKeys.length}</Typography.Text>}
        </Space>
      </Flex>

      <Table rowKey="id" expandable={{
        expandedRowRender,
      }} locale={{emptyText: 'No transactions found'}} scroll={{x: 'max-content'}} loading={loading || innerLoading} columns={columns} rowSelection={{
        selectedRowKeys,
        onChange: (rowKeys) => setSelectedRowKeys(rowKeys),
      }} dataSource={transactions || []} showSorterTooltip={false} pagination={{position: ['none', 'bottomCenter'], hideOnSinglePage: true}} {...props}/>
    </Space>
  );
};

export default TransactionsTable;
